import type { SummaryLengthOption } from '../../../../lib/link-preview/summary-options';
import { SUMMARY_LENGTH_OPTIONS } from '../../../../lib/link-preview/summary-options';
import type { SummaryModeSetting, SummarySearchMode, TranscriptSource } from '../types';

export function sanitizeSummaryLength(value: unknown): SummaryLengthOption | null {
  if (typeof value !== 'string') {
    return null;
  }
  const resolved = SUMMARY_LENGTH_OPTIONS.find((option) => option.value === value);
  return resolved?.value ?? null;
}

export function sanitizeModel(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function sanitizeBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  return null;
}

export function sanitizeNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return null;
}

const KNOWN_TRANSCRIPT_SOURCES = [
  'youtubei',
  'captionTracks',
  'youtube-media',
  'apify',
  'html',
  'unavailable',
] as const satisfies readonly TranscriptSource[];

const isTranscriptSource = (input: string): input is TranscriptSource =>
  (KNOWN_TRANSCRIPT_SOURCES as readonly string[]).includes(input);

export function sanitizeTranscriptSource(value: unknown): TranscriptSource | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return isTranscriptSource(trimmed) ? trimmed : null;
}

const SUMMARY_MODES: readonly SummaryModeSetting[] = ['summary', 'explain', 'custom'];

const isSummaryMode = (value: string): value is SummaryModeSetting =>
  (SUMMARY_MODES as readonly string[]).includes(value);

export function sanitizeSummaryMode(value: unknown): SummaryModeSetting | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return isSummaryMode(trimmed) ? trimmed : null;
}

const SUMMARY_SEARCH_MODES: readonly SummarySearchMode[] = ['auto', 'off'];

const isSummarySearchMode = (value: string): value is SummarySearchMode =>
  (SUMMARY_SEARCH_MODES as readonly string[]).includes(value);

export function sanitizeSearchMode(value: unknown): SummarySearchMode | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return isSummarySearchMode(trimmed) ? trimmed : null;
}
